<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import AddCsatCustomDomainDialog from './AddCsatCustomDomainDialog.vue';
import CsatDNSConfigurationDialog from './CsatDNSConfigurationDialog.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const addCustomDomainDialogRef = ref(null);
const dnsConfigurationDialogRef = ref(null);
const updatedDomainAddress = ref('');

const customDomainName = computed(() => props.inbox?.csat_custom_domain || '');

const openAddCustomDomainDialog = () => {
  addCustomDomainDialogRef.value?.dialogRef?.open();
};

const handleAddCustomDomain = async domain => {
  await emit('update', { csat_custom_domain: domain });
  addCustomDomainDialogRef.value?.dialogRef?.close();

  if (domain) {
    updatedDomainAddress.value = domain;
    dnsConfigurationDialogRef.value?.dialogRef?.open();
  }
};

const handleRemoveCustomDomain = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('INBOX_MGMT.CSAT.CUSTOM_DOMAIN.REMOVE_CONFIRMATION'))) {
    return;
  }

  await emit('update', { csat_custom_domain: null });
};

const closeDNSConfigurationDialog = () => {
  updatedDomainAddress.value = '';
  dnsConfigurationDialogRef.value?.dialogRef?.close();
};
</script>

<template>
  <div class="flex flex-col w-full gap-6">
    <!-- Header -->
    <div class="flex flex-col gap-2">
      <h6 class="text-base font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.CSAT.CUSTOM_DOMAIN.HEADER') }}
      </h6>
      <span class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.CSAT.CUSTOM_DOMAIN.DESCRIPTION') }}
      </span>
    </div>

    <!-- Custom Domain Display/Actions -->
    <div class="flex flex-col w-full gap-4">
      <div class="flex items-center justify-between w-full gap-2">
        <!-- Domain Info -->
        <div v-if="customDomainName" class="flex flex-col gap-1">
          <div class="flex items-center w-full h-8 gap-4">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('INBOX_MGMT.CSAT.CUSTOM_DOMAIN.LABEL') }}:
            </label>
            <span class="text-sm text-n-slate-12">
              {{ customDomainName }}
            </span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center gap-4">
          <div v-if="customDomainName" class="flex items-center gap-3">
            <!-- Edit Button -->
            <Button
              icon="i-lucide-pencil"
              color="slate"
              size="sm"
              variant="link"
              :label="t('INBOX_MGMT.CSAT.CUSTOM_DOMAIN.EDIT')"
              class="hover:!no-underline flex-shrink-0"
              @click="openAddCustomDomainDialog"
            />
          </div>

          <!-- Add/Remove Button -->
          <Button
            v-if="!customDomainName"
            :label="t('INBOX_MGMT.CSAT.CUSTOM_DOMAIN.ADD')"
            color="slate"
            size="sm"
            @click="openAddCustomDomainDialog"
          />
          <Button
            v-else
            icon="i-lucide-trash"
            :label="t('INBOX_MGMT.CSAT.CUSTOM_DOMAIN.REMOVE')"
            color="ruby"
            size="sm"
            variant="link"
            class="hover:!no-underline"
            @click="handleRemoveCustomDomain"
          />
        </div>
      </div>
    </div>

    <!-- Dialogs -->
    <AddCsatCustomDomainDialog
      ref="addCustomDomainDialogRef"
      :mode="customDomainName ? 'edit' : 'add'"
      :custom-domain="customDomainName"
      @add-custom-domain="handleAddCustomDomain"
    />

    <CsatDNSConfigurationDialog
      ref="dnsConfigurationDialogRef"
      :custom-domain="updatedDomainAddress || customDomainName"
      @close="closeDNSConfigurationDialog"
    />
  </div>
</template>
